import useStore from "../Store/Store";
import { logger } from "./Logger";

// A small helper to parse edges from the raw XML,
// returning an array of { id, source, target } objects
function parseEdgesFromXml(xmlString: string) {
  // First, let's properly parse all cells with their attributes
  const cellRegex = /<mxCell[^>]*>/g;
  const userObjectRegex = /<UserObject[^>]*>[\s\S]*?<\/UserObject>/g;
  const idRegex = /id="([^"]*)"/;
  const sourceRegex = /source="([^"]*)"/;
  const targetRegex = /target="([^"]*)"/;
  const parentRegex = /parent="([^"]*)"/;
  const styleRegex = /style="[^"*]"/;
  const vertexRegex = /vertex="1"/;
  const edgeRegex = /edge="1"/;

  const edgeResults: {
    id: string | null;
    source: string | null;
    target: string | null;
    parent: string | null;
  }[] = [];
  const parentChildMap = new Map<string, string>();
  const vertexIds = new Set<string>();

  // Extract all UserObjects and their nested mxCells
  const userObjects = xmlString.match(userObjectRegex) || [];

  // Process all cells, including those nested in UserObjects
  const allCells = [...(xmlString.match(cellRegex) || [])];
  userObjects.forEach((userObj) => {
    const userIdMatch = userObj.match(idRegex);
    const userId = userIdMatch ? userIdMatch[1] : null;
    const nestedCells = userObj.match(cellRegex) || [];
    nestedCells.forEach((cell) => {
      // Add the UserObject ID to the nested mxCell if it doesn't have one
      if (!idRegex.test(cell) && userId) {
        const cellWithId = cell.replace(/<mxCell/, `<mxCell id="${userId}"`);
        allCells.push(cellWithId);
      } else {
        allCells.push(cell);
      }
    });
  });

  // First pass: identify all vertices and edges
  allCells.forEach((cellXml) => {
    const idMatch = cellXml.match(idRegex);
    const id = idMatch ? idMatch[1] : null;

    if (!id) return;

    // Check if it's a vertex - either explicitly marked or has style but not an edge
    const isVertex =
      vertexRegex.test(cellXml) ||
      (styleRegex.test(cellXml) && !edgeRegex.test(cellXml));

    if (isVertex) {
      vertexIds.add(id);
      // Get parent relationship
      const parentMatch = cellXml.match(parentRegex);
      if (parentMatch) {
        const parent = parentMatch[1];
        if (parent !== "1" && parent !== "0") {
          // Ignore root containers
          parentChildMap.set(id, parent);
        }
      }
    }

    // Check if it's an edge
    if (edgeRegex.test(cellXml)) {
      const sourceMatch = cellXml.match(sourceRegex);
      const targetMatch = cellXml.match(targetRegex);
      const parentMatch = cellXml.match(parentRegex);

      edgeResults.push({
        id,
        source: sourceMatch ? sourceMatch[1] : null,
        target: targetMatch ? targetMatch[1] : null,
        parent: parentMatch ? parentMatch[1] : null,
      });
    }
  });

  return {
    edges: edgeResults,
    parentChildMap,
    vertexIds,
  };
}

// Helper function to get all descendant cells recursively
function getAllDescendants(
  cellId: string,
  parentChildMap: Map<string, string>,
  result: Set<string> = new Set()
): Set<string> {
  // Get immediate children
  for (const [childId, parentId] of parentChildMap.entries()) {
    if (parentId === cellId) {
      result.add(childId);
      // Recursively get descendants of this child
      getAllDescendants(childId, parentChildMap, result);
    }
  }

  return result;
}

/**
 * A DFS that *only* continues in one direction ('incoming' or 'outgoing').
 */
function directionalDFS(
  currentCellId: string,
  edges: {
    id: string;
    source: string;
    target: string;
    parent: string | null;
  }[],
  parentChildMap: Map<string, string>,
  vertexIds: Set<string>,
  visited: Set<string>,
  direction: "incoming" | "outgoing",
  level: number,
  maxLevels: number,
  result: string[]
) {
  // Stop if we reach max depth
  if (level > maxLevels) {
    return;
  }

  // Record that we visited this cell in this direction
  const visitedKey = `${currentCellId}:${direction}`;
  if (!visited.has(visitedKey)) {
    visited.add(visitedKey);
    result.push(currentCellId);
    // If this is a vertex, process all its descendants
    if (vertexIds.has(currentCellId)) {
      // Get all descendants (children, grandchildren, etc.)
      const descendants = getAllDescendants(currentCellId, parentChildMap);

      // Add all descendants at the same level
      for (const descendantId of descendants) {
        if (!visited.has(`${descendantId}:${direction}`)) {
          visited.add(`${descendantId}:${direction}`);
          result.push(descendantId);

          // Get edges connected to this descendant
          const descendantEdges = edges.filter(
            (edge) =>
              (direction === "outgoing" && edge.source === descendantId) ||
              (direction === "incoming" && edge.target === descendantId)
          );

          // Process each edge connected to the descendant
          for (const edge of descendantEdges) {
            if (!visited.has(edge.id!)) {
              visited.add(edge.id!);
              result.push(edge.id!);

              // Get the next cell to visit based on direction
              const nextCellId =
                direction === "incoming" ? edge.source : edge.target;
              if (nextCellId && !visited.has(`${nextCellId}:${direction}`)) {
                // Continue DFS from the connected cell
                directionalDFS(
                  nextCellId,
                  edges,
                  parentChildMap,
                  vertexIds,
                  visited,
                  direction,
                  level + 1,
                  maxLevels,
                  result
                );
              }
            }
          }
        }
      }
    }
  }

  // Process edges connected to current cell
  const relevantEdges = edges.filter(
    (edge) =>
      (direction === "outgoing" && edge.source === currentCellId) ||
      (direction === "incoming" && edge.target === currentCellId)
  );

  for (const edge of relevantEdges) {
    if (edge.id && !visited.has(edge.id)) {
      visited.add(edge.id);
      result.push(edge.id);

      const nextCellId = direction === "incoming" ? edge.source : edge.target;
      if (nextCellId && !visited.has(`${nextCellId}:${direction}`)) {
        directionalDFS(
          nextCellId,
          edges,
          parentChildMap,
          vertexIds,
          visited,
          direction,
          level + 1,
          maxLevels,
          result
        );
      }
    }
  }
}

/**
 * Returns a flat array of all cells+edges found when performing directional DFS
 * from the provided cells or the selected cell.
 *
 * Handles both:
 * - Node selection: same as your current logic
 * - Edge selection: we perform a two-sided DFS (incoming from source, outgoing from target).
 *
 * @param maxLevels Maximum number of levels to traverse in the graph
 * @param cellIds Optional array of cell IDs to get connections for. If not provided, uses the currently selected cell.
 * @returns A flat array of cell IDs representing all connected cells
 */
export function getConnectedCellIds(
  maxLevels = Infinity,
  cellIds: string[] | null = null
): string[] {
  logger.info(
    "getConnectedCellIds with maxLevels:",
    maxLevels,
    "cellIds:",
    cellIds
  );

  // 1. Grab the selected cell (if no cellIds provided) & the raw XML
  const selectedCell = useStore.getState().selectedCell;
  const xmlString = useStore.getState().diagramData.drawioXML;
  const { edges, parentChildMap, vertexIds } = parseEdgesFromXml(xmlString);

  // Our final "flat" result
  const result: string[] = [];
  // We'll keep track of visited items in a Set so we don't repeat edges/cells
  const visited = new Set<string>();

  // If cellIds is provided, process each cell in the array
  const cellsToProcess = cellIds || (selectedCell ? [selectedCell] : []);

  if (cellsToProcess.length === 0) {
    // If there is no cell selected and no cellIds provided, return empty
    return [];
  }

  // Process each cell
  for (const cellId of cellsToProcess) {
    if (!cellId) continue;

    // Check if the cell is an actual edge
    const selectedEdge = edges.find((edge) => edge.id === cellId);

    if (selectedEdge) {
      //
      // ============== EDGE PROCESSING ==============
      //
      logger.info("Processing an EDGE with id:", cellId);

      // 1. Add the edge itself if not already visited
      if (!visited.has(selectedEdge.id)) {
        visited.add(selectedEdge.id);
        result.push(selectedEdge.id);
      }

      // 2. If a valid source exists, run DFS "incoming" from it
      if (
        selectedEdge.source &&
        !visited.has(`${selectedEdge.source}:incoming`)
      ) {
        directionalDFS(
          selectedEdge.source,
          edges,
          parentChildMap,
          vertexIds,
          visited,
          "incoming",
          1,
          maxLevels,
          result
        );
      }

      // 3. If a valid target exists, run DFS "outgoing" from it
      if (
        selectedEdge.target &&
        !visited.has(`${selectedEdge.target}:outgoing`)
      ) {
        directionalDFS(
          selectedEdge.target,
          edges,
          parentChildMap,
          vertexIds,
          visited,
          "outgoing",
          1,
          maxLevels,
          result
        );
      }
    } else {
      //
      // ============== NODE PROCESSING ==============
      //
      logger.info("Processing a NODE with id:", cellId);

      // First, add the selected node itself if not already visited
      if (
        !visited.has(`${cellId}:incoming`) &&
        !visited.has(`${cellId}:outgoing`)
      ) {
        result.push(cellId);
        visited.add(`${cellId}:incoming`);
        visited.add(`${cellId}:outgoing`);
      }

      // Get and add all descendants
      const descendants = getAllDescendants(cellId, parentChildMap);

      // Add all descendants and mark them as visited in both directions
      for (const descendantId of descendants) {
        if (
          !visited.has(`${descendantId}:incoming`) &&
          !visited.has(`${descendantId}:outgoing`)
        ) {
          if (!result.includes(descendantId)) {
            result.push(descendantId);
          }
          visited.add(`${descendantId}:incoming`);
          visited.add(`${descendantId}:outgoing`);
        }
      }

      // Get all edges connected to either the selected node or its descendants
      const allRelevantIds = new Set([cellId, ...descendants]);
      const connectedEdges = edges.filter(
        (edge) =>
          allRelevantIds.has(edge.source!) || allRelevantIds.has(edge.target!)
      );

      // Process each connected edge
      connectedEdges.forEach((edge) => {
        if (edge.id && !visited.has(edge.id)) {
          visited.add(edge.id);
          result.push(edge.id);
        }

        // For edges where we are the target, traverse incoming
        if (allRelevantIds.has(edge.target!) && edge.source) {
          if (!visited.has(`${edge.source}:incoming`)) {
            directionalDFS(
              edge.source,
              edges,
              parentChildMap,
              vertexIds,
              visited,
              "incoming",
              1,
              maxLevels,
              result
            );
          }
        }

        // For edges where we are the source, traverse outgoing
        if (allRelevantIds.has(edge.source!) && edge.target) {
          if (!visited.has(`${edge.target}:outgoing`)) {
            directionalDFS(
              edge.target,
              edges,
              parentChildMap,
              vertexIds,
              visited,
              "outgoing",
              1,
              maxLevels,
              result
            );
          }
        }
      });
    }
  }

  return result;
}

/**
 * Centers the graph in drawio. If there are selected cells, centers around those cells.
 * If no cells are selected, centers the graph to show all cells.
 *
 * @returns A promise that resolves when the graph has been centered
 */
export async function centerGraph(): Promise<void> {
  try {
    const selectedCell = useStore.getState().selectedCell;
    if (useStore.getState().debugMode) {
      logger.debug.info(
        `Centering graph${
          selectedCell ? ` around cell: ${selectedCell}` : " to show all cells"
        }`
      );
    }

    const response = await useStore.getState().postToDrawioWaitForResponse({
      action: "CENTER_GRAPH",
      data: {
        selectedCell,
      },
    });

    if (response?.status === "ERROR") {
      logger.error("Error centering graph in drawio", response);
      throw new Error("Failed to center graph");
    }
  } catch (error) {
    logger.error("Error in centerGraph", error);
    throw error;
  }
}
